import { z } from 'zod'
import { SKILL_GAMES } from './skillLabels.js'

/**
 * A round she has finished, told well enough to recognise.
 *
 * Not a table of rows. "MONDAY, a few minutes ago" is a memory; "hangman ·
 * won · 47s" is an audit log, and nobody looks at an audit log twice.
 *
 * `detail` is what makes a round that one rather than any other — the word
 * solved, the size of the board. It is worked out by dealing the round again
 * from the seed the server issued rather than stored, which the engines being
 * pure and seeded makes exact and free.
 */
export const playedRoundSchema = z
  .object({
    id: z.string(),
    gameKey: z.string(),
    result: z.enum(['won', 'lost', 'abandoned']),
    difficulty: z.string(),
    /**
     * Whether she played it on her own or in a room.
     *
     * Plain text rather than an enum, like the column it comes from, and
     * defaulted rather than required: a response from a build that predates
     * this field would otherwise fail to parse and take the whole history page
     * with it, which is a worse answer than "assume it was solo" — and solo is
     * what every round was until rooms existed.
     */
    mode: z.string().default('solo'),
    durationMs: z.number().int().min(0),
    playedAt: z.string(),
    /** Null when the round it came from has been swept up, or cannot be re-dealt. */
    detail: z.string().nullable(),
  })
  .strict()

export type PlayedRound = z.infer<typeof playedRoundSchema>

export const recentPlaysSchema = z
  .object({
    rounds: z.array(playedRoundSchema),
    /**
     * How many separate days she has played on.
     *
     * Not a streak. A streak turns a missed day into a loss, and the day she
     * most needs the app to be friendly is the day she has not opened it for a
     * week. A count of days only ever goes up, and says the same warm thing.
     */
    daysPlayed: z.number().int().min(0),
  })
  .strict()

export type RecentPlays = z.infer<typeof recentPlaysSchema>

/**
 * How hard a round was, as it should be said to the player — or nothing.
 *
 * A reading band and a board size describe the round. A level describes *her*,
 * and putting that on a line in her own history is a score by another name —
 * the more so sitting a few centimetres below a card that deliberately refuses
 * to name a level at all.
 *
 * So a game whose difficulty is a level says nothing here. What changed is
 * which questions she gets, and that is said once, gently, where it belongs.
 *
 * This used to read `if (gameKey === 'maths')`, written when Monkey was the
 * only game that kept levels. Turtley and Rocky grew them since, and both
 * quietly started printing "level 4" on her own page — for two releases, under
 * a comment explaining why that must never happen.
 */
export function roundDifficulty(round: {
  readonly gameKey: string
  readonly difficulty: string
  readonly mode: string
}): string | null {
  /*
   * A race is not a level.
   *
   * One key covers two different games. Solo Monkey files `level 6`, which is
   * a fact about her; a maths race in a room files `just-right`, which is a
   * tier the host picked before anybody played and describes the round exactly
   * as a reading band or a board size does.
   *
   * Both were silenced, because this was handed a key and a key cannot tell
   * them apart. The row knows — it has said `solo` or `multi` since plays were
   * first recorded — it was simply never asked.
   */
  return round.mode === 'solo' && describesThePlayer(round.gameKey) ? null : round.difficulty
}

/**
 * Whether this game's difficulty is a fact about her rather than about the round.
 *
 * Read off the list of games that keep per-skill levels, rather than a second
 * list of game keys kept here — so a game added to `SKILL_GAMES` is silenced
 * without anybody editing this file, which is the point of doing it this way.
 *
 * It is a good rule rather than a law. It applies to a round she played on her
 * own: a game that adapts to her files where she has got to, and that is the
 * thing this refuses to print. A race in a room files the tier the host chose,
 * which describes the round and is shown — see `roundDifficulty`, which is
 * handed the round rather than only its key for exactly that reason.
 */
function describesThePlayer(gameKey: string): boolean {
  return (SKILL_GAMES as readonly string[]).includes(gameKey)
}

/** How long a round took, in words rather than milliseconds. */
export function roundLength(durationMs: number): string {
  const seconds = Math.round(durationMs / 1000)
  if (seconds < 60) return `${seconds}s`

  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60

  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`
}

/**
 * When it was, in the terms an eight-year-old thinks in.
 *
 * Days, not timestamps. "Yesterday" is a thing she remembers doing; "22 Aug,
 * 18:04" is a thing that happened to somebody.
 */
export function whenItWas(playedAt: string, now: number = Date.now()): string {
  const at = new Date(playedAt).getTime()
  if (!Number.isFinite(at)) return 'a while back'

  const minutes = Math.floor((now - at) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`

  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`

  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`

  return 'a while back'
}
